import { format, toZonedTime } from 'date-fns-tz';
import qs from 'qs';

import { correctLang } from '@/api/cms/base';
import { ONE_MINUTE } from '@/helpers/dateTime';

const CMS_TIME_WINDOW = 5 * ONE_MINUTE;

export const getCMSTimeWindow = (date = new Date()) =>
  new Date(Math.floor(date.getTime() / CMS_TIME_WINDOW) * CMS_TIME_WINDOW);

// For client SWR keys: a per-render time would change the key every window and
// blank the lists while they refetch. Server queries take the request's window.
export const CMS_CLIENT_QUERY_TIME = getCMSTimeWindow();

export const getCMSSchedulerQuery = (at: Date) => ({
  $or: [
    {
      scheduler: {
        id: {
          $null: true,
        },
      },
    },
    {
      scheduler: {
        weekday: {
          $eq: format(toZonedTime(at, 'UTC'), 'EEEE'),
        },
      },
    },
  ],
});

export const getCMSDurationQuery = (at: Date) => {
  const now = at.toISOString();

  return {
    $and: [
      {
        $or: [
          { duration_from: { $null: true } },
          { duration_from: { $lt: now } },
        ],
      },
      {
        $or: [{ duration_to: { $null: true } }, { duration_to: { $gt: now } }],
      },
    ],
  };
};

export const getCMSCountryQuery = (countryCode: string) => [
  {
    $or: [
      { country_codes_exclude: { $null: true } },
      { country_codes_exclude: { $eq: '' } },
      { country_codes_exclude: { $not: { $contains: countryCode } } },
    ],
  },
  {
    $or: [
      { country_codes_include: { $null: true } },
      { country_codes_include: { $eq: '' } },
      { country_codes_include: { $contains: countryCode } },
    ],
  },
];

export const getCMSShowGuestQuery = (isAuthenticated: boolean) =>
  isAuthenticated ? [] : [{ show_guest_toggle: { $eq: true } }];

export const getCMSImageFieldsUrl = (name: string) => ({
  [name]: {
    fields: ['url'],
  },
});

export const getCMSLocale = (language: string) => ({
  locale: [correctLang(language)],
});

export enum CMSSortTypeEnum {
  ASC = 'asc',
  DESC = 'desc',
}

export interface ICMSQuery {
  fields?: string[];
  sort?: string | string[];
  filters?: Record<string, any>;
  populate?: Record<string, any> | 'deep';
  locale?: string[];
  publicationState?: 'live' | 'preview';
  pagination?: {
    page?: number;
    pageSize?: number;
    start?: number;
    limit?: number;
  };
}

export const createCMSQuery = (data: ICMSQuery) =>
  qs.stringify(data, { encodeValuesOnly: true });
